import { useState } from "react";
import moment from "moment";
import { Box, Grid, Typography } from "@mui/material";

import FileDropzone from "components/FileDropzone";
import useToaster from "hooks/useToaster";
import {
  downloadTemplateFile,
  fetchVendorOffDays,
  saveVendorOffDays,
  uploadVendorOffDaysFile,
} from "services/eddService";
import {
  EDD_TEMPLATE_NAMES,
  OFF_DAYS_FAILED_ROW_COLUMNS,
  UPLOAD_FORMAT_HINT,
} from "constants/eddConfig";
import { getRowResultToast } from "utils/eddUtils";
import EddSectionCard from "../components/EddSectionCard";
import VendorSearch from "../VendorSearch";
import VendorInfoCard from "../VendorWorkingHours/components/VendorInfoCard";
import AddOffDayForm from "../AddOffDayForm";
import OffDaysTable from "../OffDaysTable";
import RowResultDialog from "../RowResultDialog";
import { useEddUploadContext } from "../EddUploadContext";

const SIDE_BY_SIDE_CARD_SX = {
  mb: 0,
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

const sortByDate = (rows = []) =>
  [...rows].sort((a, b) => moment(a.date).diff(moment(b.date)));

const VendorOffDays = () => {
  const triggerToaster = useToaster();
  const currentYear = moment().year();

  const [vendor, setVendor] = useState(null);
  const [offDays, setOffDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { isGlobalUploading, setIsGlobalUploading } = useEddUploadContext();
  const isBusy = isUploading || Boolean(isGlobalUploading);
  const [uploadResult, setUploadResult] = useState(null);

  // API returns every off day of the vendor, table is scoped to current year
  const yearOffDays = offDays.filter(
    (row) => moment(row.date).year() === currentYear,
  );

  const loadOffDays = async (vendorId) => {
    setIsLoading(true);
    const result = await fetchVendorOffDays(vendorId);
    if (result.error) triggerToaster(result.error, "error");
    setOffDays(sortByDate(result.data));
    setIsLoading(false);
  };

  const handleVendorChange = (option) => {
    setVendor(option);
    setUploadResult(null);
    if (!option) {
      setOffDays([]);
      return;
    }
    loadOffDays(option.vendor_id);
  };

  const saveOffDay = async (offDay, successMessage) => {
    const result = await saveVendorOffDays(vendor.vendor_id, [offDay]);
    if (result.error) {
      triggerToaster(result.error, "error");
      return false;
    }
    const failedRow = result.data.failedRows[0];
    if (result.data.failedCount || !result.data.successCount) {
      triggerToaster(failedRow?.message || "Failed to save off day", "error");
      return false;
    }
    triggerToaster(successMessage, "success");
    loadOffDays(vendor.vendor_id);
    return true;
  };

  const handleAdd = async (offDay) => {
    if (offDays.some((row) => row.is_active && row.date === offDay.date)) {
      triggerToaster(
        "Off day already exists for this date, edit it instead",
        "error",
      );
      return false;
    }
    setIsAdding(true);
    const isSuccess = await saveOffDay(offDay, "Off day added successfully");
    setIsAdding(false);
    return isSuccess;
  };

  const handleUpdate = async (offDay) => {
    setSavingId(offDay.id ?? offDay.date);
    const isSuccess = await saveOffDay(offDay, "Off day updated successfully");
    setSavingId(null);
    return isSuccess;
  };

  const handleFileUpload = async (file) => {
    if (isBusy) return false;
    setIsUploading(true);
    setIsGlobalUploading?.(true);
    try {
      const result = await uploadVendorOffDaysFile(vendor.vendor_id, file);
      if (result.error) {
        triggerToaster(result.error, "error");
        return false;
      }
      triggerToaster(...getRowResultToast(result.data, "Upload"));
      if (result.data.failedCount) setUploadResult(result.data);
      if (result.data.successCount) loadOffDays(vendor.vendor_id);
      return true;
    } finally {
      setIsUploading(false);
      setIsGlobalUploading?.(false);
    }
  };

  const handleDownloadTemplate = async () => {
    const result = await downloadTemplateFile(
      EDD_TEMPLATE_NAMES.VENDOR_OFF_DAYS,
    );
    if (result && !result.success) {
      triggerToaster(result.error || "Failed to download template", "error");
    }
  };

  return (
    <Box>
      <Box mb={6}>
        <Typography fontSize={18} fontWeight="bold">
          Vendor Off Days / Holidays
        </Typography>
        <Typography fontSize={14} color="text.secondary">
          Per-date overrides on top of the weekly baseline for a single vendor.
          Add individual dates inline, or upload a CSV scoped to this vendor's
          off-day list.
        </Typography>
      </Box>

      <EddSectionCard>
        <Grid container spacing={8}>
          <Grid item xs={12} md={7}>
            <VendorSearch vendor={vendor} onVendorChange={handleVendorChange} />
          </Grid>
        </Grid>
        {vendor && (
          <VendorInfoCard
            vendor={vendor}
            stats={[
              {
                label: `Off days ${currentYear}`,
                value: isLoading ? "-" : yearOffDays.length,
              },
            ]}
          />
        )}
      </EddSectionCard>

      {vendor && (
        <>
          <Grid container spacing={8} mb={8}>
            <Grid item xs={12} md={7}>
              <EddSectionCard title="Add Off Day" sx={SIDE_BY_SIDE_CARD_SX}>
                <AddOffDayForm
                  onAdd={handleAdd}
                  isSubmitting={isAdding}
                  maxDate={moment().endOf("year").format("YYYY-MM-DD")}
                />
              </EddSectionCard>
            </Grid>
            <Grid item xs={12} md={5}>
              <EddSectionCard sx={SIDE_BY_SIDE_CARD_SX}>
                <FileDropzone
                  fillHeight
                  title="Bulk upload"
                  subtitle={vendor.vendor_id}
                  hint={UPLOAD_FORMAT_HINT}
                  acceptedTypes={["csv"]}
                  maxSizeMB={5}
                  onUpload={handleFileUpload}
                  onDownloadTemplate={handleDownloadTemplate}
                  isUploading={isUploading}
                  disabled={isBusy}
                />
              </EddSectionCard>
            </Grid>
          </Grid>

          <RowResultDialog
            open={Boolean(uploadResult)}
            title="Bulk upload failed rows"
            result={uploadResult}
            columns={OFF_DAYS_FAILED_ROW_COLUMNS}
            onClose={() => setUploadResult(null)}
          />

          <EddSectionCard title={`Off Days — ${currentYear}`}>
            <OffDaysTable
              rows={yearOffDays}
              isLoading={isLoading}
              savingId={savingId}
              onSave={handleUpdate}
            />
          </EddSectionCard>
        </>
      )}
    </Box>
  );
};

export default VendorOffDays;
